/* eslint-disable no-continue */
/* eslint-disable no-restricted-syntax */
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useSendbirdStateContext } from '@sendbird/uikit-react';
import {
  broadcastedMessageType,
  ClientChatTypes,
  HelpUserTypes,
  setClientTabUnreadCount,
  setUnreadCount,
  TypeOfUser,
  userIdSelector,
} from '@pharmaplan/common';
import GroupChannelHandler from '@sendbird/uikit-react/handlers/GroupChannelHandler';
import { useAppSelector } from '../../useAppSelector';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import audio from '../../../assets/audio/live_chat.mp3';

let prevTotal = 0;

/*
  If message is sent as broadcast, it does not contain channel customType as customType
  is added when a user/admin messages on the chat. In that case we need to check if the
  last message is a broadcast message and then check the user_type metadata to determine
  the type of user that sent the message. If the last message is not a broadcast message,
  we can use the channel customType to determine the type of user that sent the message.
  ONLY APPLIES TO SELF/PG.
*/
const getBroadcastType = (
  channel: { lastMessage?: { customType?: string } | null },
  metadataUserType?: string,
) => {
  if (channel.lastMessage?.customType !== broadcastedMessageType) {
    return null;
  }

  const broadcastUserType = HelpUserTypes[metadataUserType as keyof typeof HelpUserTypes] ?? null;

  if (broadcastUserType) {
    return `+${broadcastUserType}`;
  }

  if (metadataUserType?.split(',').length === 2) {
    return broadcastedMessageType;
  }

  return null;
};

function useAdminUnreadChats() {
  const dispatch = useDispatch();
  const store = useSendbirdStateContext();
  const sdk = store?.stores?.sdkStore?.sdk;

  const notificationSound = new Audio(audio);

  const channelsRef = useRef<any[]>([]);
  const userId: string = useAppSelector(userIdSelector);

  /**
   * Fetch all channels once
   */
  const fetchAllChannels = async () => {
    if (!sdk) return [];

    const query = sdk.groupChannel?.createMyGroupChannelListQuery({
      includeEmpty: true,
      includeFrozen: true,
      includeMetaData: true,
      limit: 100,
    });

    const all: any[] = [];

    while (query?.hasNext) {
      // eslint-disable-next-line no-await-in-loop
      const channels = await query.next();
      all.push(...channels);
    }

    channelsRef.current = all;

    return all;
  };

  /**
   * Core aggregation logic
   */
  const computeCounts = (channels: any[], playSound = true) => {
    let pharmacist = 0;
    let technician = 0;
    let student = 0;
    let pharmacy = 0;
    let total = 0;

    for (const ch of channels) {
      const meta = ch.cachedMetaData || ch.metaData || {};

      const { users, user_type: metadataUserType } = meta;

      const type = ch.customType || getBroadcastType(ch, metadataUserType);

      const unread = ch.unreadMessageCount || 0;

      if (!type) continue;

      if (users && users !== userId) continue;

      total += unread;

      switch (type) {
        case `+${TypeOfUser.pharmacist}`:
        case `${userId}+${TypeOfUser.pharmacist}`:
          pharmacist += unread;
          break;
        case `+${TypeOfUser.technician}`:
        case `${userId}+${TypeOfUser.technician}`:
          technician += unread;
          break;
        case `+${TypeOfUser.student}`:
        case `${userId}+${TypeOfUser.student}`:
          student += unread;
          break;
        case `+${TypeOfUser.pharmacy}`:
        case `${userId}+${TypeOfUser.pharmacy}`:
          pharmacy += unread;
          break;
        default:
          break;
      }
    }

    const knownSum = pharmacist + technician + student + pharmacy;

    const users = total - knownSum;

    if (total > prevTotal && playSound) {
      notificationSound.play();
    }

    prevTotal = total;

    dispatch(setUnreadCount(total));

    return {
      [ClientChatTypes.pharmacist]: pharmacist,
      [ClientChatTypes.technician]: technician,
      [ClientChatTypes.student]: student,
      [ClientChatTypes.pharmacy]: pharmacy,
      [ClientChatTypes.users]: Math.max(users, 0),
    };
  };

  /**
   * Push update to Redux
   */
  const updateRedux = (channels: any[], playSound = true) => {
    const counts = computeCounts(channels, playSound);

    dispatch(setClientTabUnreadCount(counts));
  };

  /**
   * Initial load
   */
  useEffect(() => {
    if (!sdk || !userId) return;

    const init = async () => {
      const channels = await fetchAllChannels();
      updateRedux(channels, false);
    };

    init();
  }, [sdk, userId]);

  /**
   * Real-time updates
   */
  useEffect(() => {
    if (!sdk) return;

    const onMessageReceived = (channel: any) => {
      const channels = channelsRef.current;

      const index = channels.findIndex((c) =>
        c.url === channel.url);

      if (index !== -1) {
        channels[index] = channel;
      } else {
        channels.push(channel);
      }

      channelsRef.current = channels;

      updateRedux(channels);
    };

    const onChannelChanged = (channel: any) => {
      const channels = channelsRef.current;

      const index = channels.findIndex((c) =>
        c.url === channel.url);

      if (index !== -1) {
        channels[index] = channel;
      }

      channelsRef.current = channels;

      updateRedux(channels);
    };

    const handler = new GroupChannelHandler({
      onMessageReceived,
      onChannelChanged,
    });

    sdk.groupChannel?.addGroupChannelHandler('ADMIN_UNREAD_HANDLER', handler);

    // eslint-disable-next-line consistent-return
    return () => {
      sdk.groupChannel?.removeGroupChannelHandler('ADMIN_UNREAD_HANDLER');
    };
  }, [sdk, userId]);
}

export default useAdminUnreadChats;
